import {useEffect, useRef} from 'react';
import type {RefObject} from 'react';

export interface AnimationControls {
  pause: () => void;
  resume: () => void;
}

interface Options {
  // Quiet period after the last pointerdown/keydown before resuming.
  resumeDelay?: number;
}

/**
 * Keeps a decorative, always-running animation (typed headline, autoplay
 * slider) off the main thread while it can't matter, so its ticks never
 * compete with a tap for INP:
 *  - paused from the first pointerdown/keydown until the user goes quiet
 *  - paused while the element is off-screen
 *  - paused while the tab is hidden
 *
 * `pause` runs inside the pointerdown listener, so it must be cheap.
 */
const usePauseAnimation = (
  targetRef: RefObject<Element>,
  controls: AnimationControls,
  {resumeDelay = 1500}: Options = {}
) => {
  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let interacting = false;
    let visible = true;
    let running = true;
    let resumeTimer: ReturnType<typeof setTimeout> | null = null;
    let idleId: number | null = null;

    const apply = () => {
      const shouldRun = !interacting && visible && !document.hidden;
      if (shouldRun === running) return;
      running = shouldRun;
      if (shouldRun) {
        controlsRef.current.resume();
      } else {
        controlsRef.current.pause();
      }
    };

    const cancelResume = () => {
      if (resumeTimer !== null) clearTimeout(resumeTimer);
      if (idleId !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      resumeTimer = null;
      idleId = null;
    };

    const onInteraction = () => {
      cancelResume();
      interacting = true;
      apply();
      resumeTimer = setTimeout(() => {
        resumeTimer = null;
        const resume = () => {
          idleId = null;
          interacting = false;
          apply();
        };
        if (typeof window.requestIdleCallback === 'function') {
          idleId = window.requestIdleCallback(resume, {timeout: 1000});
        } else {
          resume();
        }
      }, resumeDelay);
    };

    const onVisibilityChange = () => apply();

    const listenerOptions: AddEventListenerOptions = {
      capture: true,
      passive: true,
    };
    document.addEventListener('pointerdown', onInteraction, listenerOptions);
    document.addEventListener('keydown', onInteraction, listenerOptions);
    document.addEventListener('visibilitychange', onVisibilityChange);

    let observer: IntersectionObserver | null = null;
    if (targetRef.current && typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting);
        apply();
      });
      observer.observe(targetRef.current);
    }

    return () => {
      cancelResume();
      document.removeEventListener(
        'pointerdown',
        onInteraction,
        listenerOptions
      );
      document.removeEventListener('keydown', onInteraction, listenerOptions);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      observer?.disconnect();
    };
  }, [targetRef, resumeDelay]);
};

export default usePauseAnimation;
